import { History } from 'history';
import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import { ROOT_URL } from '../../constants/RouterConstants';
import { createErrorAction, CreateErrorPayload, resolveErrorAction } from '../../store/actions/error';
import { Store } from '../../store/reducers/reducers';
import { errorMessageSelector } from '../../store/selectors/errorSelectors';
import ErrorPage from './ErrorPage';

interface StateProps {
  errorFromStore: boolean;
}

interface DispatchProps {
  onResolveErrorAction: () => void;
  onCreateErrorAction: (payload: CreateErrorPayload) => void;
}

interface Props extends StateProps, DispatchProps {
  history: History;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  historyChanged: boolean;
}

class ErrorPageBoundary extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    const { errorFromStore } = props;

    this.state = { hasError: errorFromStore, historyChanged: false };

    if (errorFromStore) this.registerHistoryListener();
  }

  public static getDerivedStateFromError(): State {
    return { hasError: true, historyChanged: false };
  }

  public static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (props.errorFromStore && !state.historyChanged && !state.hasError) return { hasError: true, historyChanged: false };
    if (state.historyChanged && !props.errorFromStore && !state.hasError) return { hasError: false, historyChanged: false };

    return null;
  }

  public componentDidUpdate(_: Props, prevState: State) {
    const { hasError } = this.state;
    if (hasError && !prevState.hasError) {
      this.registerHistoryListener();
    }
  }

  public componentDidCatch(error: Error, _: React.ErrorInfo) {
    const { onCreateErrorAction } = this.props;
    onCreateErrorAction({ errorMessage: error.message });
  }

  private handleResolve = () => {
    this.props.history.push(ROOT_URL);
  };

  private registerHistoryListener = () => {
    const { history } = this.props;
    const unregister = history.listen(() => {
      const { hasError } = this.state;
      if (hasError) {
        const { onResolveErrorAction } = this.props;
        this.setState(
          {
            hasError: false,
            historyChanged: true,
          },
          () => {
            onResolveErrorAction();
            unregister();
          }
        );
      }
    });
  };

  public render() {
    const { children } = this.props;
    const { hasError } = this.state;
    if (hasError) {
      return <ErrorPage onResolve={this.handleResolve} />;
    }
    return children;
  }
}

const mapStateToProps = (state: Store) => {
  const errorMessage = errorMessageSelector(state);
  if (errorMessage) return { errorFromStore: true };
  return { errorFromStore: false };
};

const actions = {
  onResolveErrorAction: resolveErrorAction,
  onCreateErrorAction: createErrorAction,
};

const ConnectedErrorPageBoundary = connect<StateProps, DispatchProps, object>(mapStateToProps, actions)(ErrorPageBoundary);

export default function ErrorPageBoundaryWithHistory(props: { children: ReactNode }) {
  const history = useHistory();

  return <ConnectedErrorPageBoundary {...props} history={history} />;
}
